import { Box, Typography } from "@mui/material"
import type { FC } from "react"

import type { AdjustmentHistorySearchParams } from "../schemas"

import { FilterChip } from "@/components/common/Filters"
import type {
  InventoryAdjustmentReason,
  InventoryAdjustmentStatus,
  InventoryAdjustmentType,
} from "@/graphql/codegen/graphql"
import { InventoryFilterChipGroup } from "@/screens/Inventory/components"
import type { Option } from "@/types/form-fields"
import { formatDate } from "@/utils/date"

interface AdjustmentHistoryFilterChipsProps {
  searchParams: AdjustmentHistorySearchParams
  adjustmentTypeOptions: Option<InventoryAdjustmentType>[]
  statusOptions: Option<InventoryAdjustmentStatus>[]
  reasonOptions: Option<InventoryAdjustmentReason>[]
  warehouseOptions: Option<string>[]
  adjustedByOptions: Option<string>[]
  onRemove: (key: keyof AdjustmentHistorySearchParams, value: string) => void
}

export const AdjustmentHistoryFilterChips: FC<AdjustmentHistoryFilterChipsProps> = ({
  searchParams,
  adjustmentTypeOptions,
  statusOptions,
  reasonOptions,
  warehouseOptions,
  adjustedByOptions,
  onRemove,
}) => {
  const adjustmentTypes = searchParams.adjustmentTypes ?? []
  const statuses = searchParams.statuses ?? []
  const adjustmentReasons = searchParams.adjustmentReasons ?? []
  const warehouseAreaIds = searchParams.warehouseAreaIds ?? []
  const adjustedByIds = searchParams.adjustedByIds ?? []
  const { dateFrom, dateTo } = searchParams

  const hasActiveFilters =
    !!dateFrom ||
    !!dateTo ||
    adjustmentTypes.length > 0 ||
    statuses.length > 0 ||
    adjustmentReasons.length > 0 ||
    warehouseAreaIds.length > 0 ||
    adjustedByIds.length > 0

  if (!hasActiveFilters) return null

  return (
    <Box className='flex flex-wrap items-center gap-2' data-testid='adjustment-history.filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      {dateFrom && (
        <FilterChip
          label={`Date From: ${formatDate(dateFrom)}`}
          onDelete={() => onRemove("dateFrom", "")}
          testId='adjustment-history.filter-chips.date-from'
        />
      )}

      {dateTo && (
        <FilterChip
          label={`Date To: ${formatDate(dateTo)}`}
          onDelete={() => onRemove("dateTo", "")}
          testId='adjustment-history.filter-chips.date-to'
        />
      )}

      <InventoryFilterChipGroup
        label='Adjustment Type'
        values={adjustmentTypes}
        options={adjustmentTypeOptions}
        onRemove={(value) => onRemove("adjustmentTypes", value)}
        testId='adjustment-history.filter-chips.adjustment-type'
      />

      <InventoryFilterChipGroup
        label='Status'
        values={statuses}
        options={statusOptions}
        onRemove={(value) => onRemove("statuses", value)}
        testId='adjustment-history.filter-chips.status'
      />

      <InventoryFilterChipGroup
        label='Reason'
        values={adjustmentReasons}
        options={reasonOptions}
        onRemove={(value) => onRemove("adjustmentReasons", value)}
        testId='adjustment-history.filter-chips.reason'
      />

      <InventoryFilterChipGroup
        label='Warehouse'
        values={warehouseAreaIds}
        options={warehouseOptions}
        onRemove={(value) => onRemove("warehouseAreaIds", value)}
        testId='adjustment-history.filter-chips.warehouse'
      />

      <InventoryFilterChipGroup
        label='Adjusted By'
        values={adjustedByIds}
        options={adjustedByOptions}
        onRemove={(value) => onRemove("adjustedByIds", value)}
        testId='adjustment-history.filter-chips.adjusted-by'
      />
    </Box>
  )
}
